import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, ClipboardList, Database, Gauge, LockKeyhole, LogOut, Menu, Server, Settings2, ShieldCheck, Webhook, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ApiError, api, type Session } from "./api";
import { ErrorNotice } from "./components";
import { ApiKeysPage } from "./pages/ApiKeysPage";
import { AuditPage } from "./pages/AuditPage";
import { BatchJobsPage } from "./pages/BatchJobsPage";
import { CallbackDeliveriesPage } from "./pages/CallbackDeliveriesPage";
import { CallbackSettingsPage } from "./pages/CallbackSettingsPage";
import { JobsPage } from "./pages/JobsPage";
import { NodesPage } from "./pages/NodesPage";
import { AgentReleasePage } from "./pages/AgentReleasePage";
import { AiModelsPage } from "./pages/AiModelsPage";
import { AutoscalingPage } from "./pages/AutoscalingPage";
import { AutoDlPage } from "./pages/AutoDlPage";
import { NodeStoragePage } from "./pages/NodeStoragePage";
import { OverviewPage } from "./pages/OverviewPage";
import { StoragePage } from "./pages/StoragePage";
import { StorageGcPage } from "./pages/StorageGcPage";
import { TranscodeSettingsPage } from "./pages/TranscodeSettingsPage";

const primaryNavigation = [
  { to: "/overview", label: "运行总览", icon: Gauge },
];

const navigationGroups = [
  {
    label: "Callback 管理",
    icon: Webhook,
    pathPrefix: "/callbacks",
    items: [
      { to: "/callbacks/deliveries", label: "投递记录" },
      { to: "/callbacks/settings", label: "调度配置" },
    ],
  },
  {
    label: "任务管理",
    icon: ClipboardList,
    pathPrefix: "/jobs",
    items: [
      { to: "/jobs", label: "任务中心" },
      { to: "/jobs/batch", label: "批量任务调试", operatorOnly: true },
    ],
  },
  {
    label: "转码节点",
    icon: Server,
    pathPrefix: "/nodes",
    items: [
      { to: "/nodes/status", label: "节点状态" },
      { to: "/nodes/storage", label: "缓存与磁盘" },
      { to: "/nodes/release", label: "Agent 版本" },
      { to: "/nodes/autoscaling", label: "腾讯云 AS · 普通节点" },
      { to: "/nodes/autodl", label: "AutoDL Pro · AI 节点" },
      { to: "/nodes/ai-models", label: "AI 模型" },
    ],
  },
  {
    label: "系统配置",
    icon: Database,
    pathPrefix: "/configuration",
    items: [
      { to: "/storage", label: "存储配置" },
      { to: "/storage/cleanup", label: "到期清理" },
      { to: "/transcode-settings", label: "转码设置" },
    ],
  },
  {
    label: "访问与审计",
    icon: ShieldCheck,
    pathPrefix: "/access",
    items: [
      { to: "/api-keys", label: "API Key" },
      { to: "/audit", label: "操作记录" },
    ],
  },
];

export function App() {
  const client = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const session = useQuery({ queryKey: ["session"], queryFn: () => api<Session>("/admin/v1/session") });
  if (session.isPending) return <div className="boot"><Activity className="boot__mark" /><span>正在连接控制面</span></div>;
  if (session.isError && session.error instanceof ApiError && session.error.status === 401) {
    return <LoginPage onLogin={async (value) => {
      client.setQueryData(["session"], value);
      await client.invalidateQueries();
    }} />;
  }
  if (session.isError) return <main className="fatal"><p className="eyebrow">控制台不可用</p><h1>控制面连接失败</h1><ErrorNotice error={session.error} /><p>请确认控制服务与 PostgreSQL 已正常启动。</p></main>;
  const logout = async () => {
    await api<void>("/admin/v1/logout", { method: "POST" });
    client.clear();
    window.location.assign("/");
  };
  return <div className="shell">
    <a href="#main-content" className="skip-link">跳到主要内容</a>
    <aside className={`sidebar ${menuOpen ? "sidebar--open" : ""}`}>
      <div className="brand"><span className="brand__mark">N</span><div><strong>Noise Service</strong><small>转码控制台</small></div></div>
      <button className="icon-button sidebar__close" aria-label="关闭导航" onClick={() => setMenuOpen(false)}><X size={20} /></button>
      <nav aria-label="管理导航">
        {primaryNavigation.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? "nav-link nav-link--active" : "nav-link"}><Icon size={18} />{label}</NavLink>)}
        {navigationGroups.map(({ label, icon: Icon, pathPrefix, items }) => {
          const active = pathPrefix === "/configuration"
            ? ["/storage", "/transcode-settings", "/pipeline"].some((path) => location.pathname.startsWith(path))
            : pathPrefix === "/access"
              ? ["/api-keys", "/audit"].some((path) => location.pathname.startsWith(path))
              : location.pathname.startsWith(pathPrefix);
          return <section className={`nav-group ${active ? "nav-group--active" : ""}`} aria-label={label} key={label}>
            <div className="nav-group__label"><Icon size={18} /><span>{label}</span></div>
            <div className="nav-group__items">
              {items.filter((item) => !("operatorOnly" in item && item.operatorOnly) || session.data.role !== "VIEWER").map(({ to, label: itemLabel }) => <NavLink key={to} to={to} end={to === "/jobs" || to === "/storage"} onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? "nav-sub-link nav-sub-link--active" : "nav-sub-link"}>{itemLabel}</NavLink>)}
            </div>
          </section>;
        })}
      </nav>
      <div className="sidebar__foot"><Settings2 size={16} /><div><span>{session.data.display_name}</span><small>{session.data.role}</small></div><button className="icon-button sidebar__logout" title="退出登录" aria-label="退出登录" onClick={() => void logout()}><LogOut size={17} /></button></div>
    </aside>
    <div className="workspace">
      <header className="topbar"><button className="icon-button menu-button" aria-label="打开导航" onClick={() => setMenuOpen(true)}><Menu size={20} /></button><div className="health"><span />控制面已连接</div><time>{new Intl.DateTimeFormat("zh-CN", { month: "long", day: "numeric", weekday: "short" }).format(new Date())}</time></header>
      <main id="main-content">
        <Routes>
          <Route path="/overview" element={<OverviewPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/batch" element={<BatchJobsPage role={session.data.role} />} />
          <Route path="/callbacks" element={<Navigate to="/callbacks/deliveries" replace />} />
          <Route path="/callbacks/deliveries" element={<CallbackDeliveriesPage role={session.data.role} />} />
          <Route path="/callbacks/settings" element={<CallbackSettingsPage role={session.data.role} />} />
          <Route path="/nodes" element={<Navigate to="/nodes/status" replace />} />
          <Route path="/nodes/status" element={<NodesPage role={session.data.role} />} />
          <Route path="/nodes/storage" element={<NodeStoragePage role={session.data.role} />} />
          <Route path="/nodes/release" element={<AgentReleasePage role={session.data.role} />} />
          <Route path="/nodes/autoscaling" element={<AutoscalingPage role={session.data.role} />} />
          <Route path="/nodes/autodl" element={<AutoDlPage role={session.data.role} />} />
          <Route path="/nodes/ai-models" element={<AiModelsPage role={session.data.role} />} />
          <Route path="/storage" element={<StoragePage role={session.data.role} />} />
          <Route path="/storage/cleanup" element={<StorageGcPage role={session.data.role} />} />
          <Route path="/transcode-settings" element={<TranscodeSettingsPage role={session.data.role} />} />
          <Route path="/pipeline" element={<Navigate to="/transcode-settings" replace />} />
          <Route path="/api-keys" element={<ApiKeysPage role={session.data.role} />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="*" element={<Navigate to="/overview" replace />} />
        </Routes>
      </main>
    </div>
  </div>;
}

function LoginPage({ onLogin }: { onLogin: (session: Session) => Promise<void> }) {
  const [error, setError] = useState<unknown>(null);
  const login = useMutation({
    mutationFn: (body: { username: string; password: string }) =>
      api<Session>("/admin/v1/login", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: onLogin,
    onError: setError,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const data = new FormData(event.currentTarget);
    login.mutate({ username: String(data.get("username")), password: String(data.get("password")) });
  };
  return <main className="login-page">
    <section className="login-panel">
      <div className="login-brand"><span className="brand__mark">N</span><div><strong>Noise Service</strong><small>转码控制台</small></div></div>
      <div className="login-copy"><p className="eyebrow"><LockKeyhole size={14} />AUTHORIZED ACCESS</p><h1>登录控制中心</h1><p>使用服务端环境变量中配置的管理员账号。</p></div>
      {error instanceof ApiError && error.status === 401
        ? <div className="notice notice--error" role="alert">账号或密码不正确，请重新输入。</div>
        : error !== null && <ErrorNotice error={error} />}
      <form className="login-form" onSubmit={submit}>
        <label className="field">管理员账号<input name="username" autoComplete="username" autoFocus required maxLength={128} /></label>
        <label className="field">密码<input name="password" type="password" autoComplete="current-password" required /></label>
        <button className="button button--primary" type="submit" disabled={login.isPending}>{login.isPending ? "正在登录…" : "登录"}</button>
      </form>
      <p className="login-foot">单管理员模式 · 会话保存在安全 Cookie 中</p>
    </section>
  </main>;
}
